import {
  Autocomplete,
  Button,
  Chip,
  Drawer,
  Grid,
  Slider,
  Popover,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Avatar,
  Tabs,
  Tab,
} from "@mui/material";
import styles from "../../Styles/task.module.css";
import genericStyles from "../../Styles/genericStyles.module.css";
import { Fragment, useContext, useEffect, useState } from "react";
import { DrawerStateContext } from "../../Context/DrawerStateContext";
import { TaskContext } from "../../Context/TaskContext";
import { TaskProps } from "../../Utils/Types";
import CircularProgressWithLabel from "../Widgets/ProgressWidget";
import { Vocabulary } from "../../Utils/Vocabulary";
import { AddAlert, Home, KeyboardTab } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TaskModel, TaskValidationSchema } from "../../Utils/Models";
import { CustomThemeContext } from "../../Context/CustomThemeProvider";
import PropertyContainer from "./PropertyContainer";
import {
  EModelTypes,
  EPriorityColors,
  ETaskPeriodicity,
  ETaskPriority,
  ETaskStatus,
  ETaskType,
} from "../../Utils/Enums";
import { getNumberOfLinesFromText, GreenSwitch, onFormError } from "../../Utils/Utils";
import useCustomMemo from "../../Hooks/useCustomMemo";
import TaskPeriodicity from "./TaskPeriodicity";
import { endpoints } from "../../Utils/UrlEnum";
import SubtasksModal from "./SubtasksModal";
import { getData } from "../../Services/getData";
import { AxiosResponse } from "axios";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { RefreshDataContext } from "../../Context/RefreshDataContext";
import FileExplorerMin from "../Widgets/FileExplorerMin";
import { ComponentsNames, FormsIds } from "../../Utils/Constants";
import { format } from "date-fns";
import Config from "../../Utils/Config";
import Alerts from "../Alerts/Alerts";

export default function Task(props: TaskProps) {
  const [shouldFieldsBeDisabled, setShouldFieldsBeDisabled] = useState(false);
  const [openPeriodicity, setOpenPeriodicity] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [users, setUsers] = useState([]);
  const drawerStateContext = useContext(DrawerStateContext);
  const themeContext: any = useContext(CustomThemeContext);
  const refreshContext = useContext(RefreshDataContext);
  const taskContext = useContext(TaskContext);
  const memo = useCustomMemo();
  const taskStatuses = memo?.storage.taskStatuses || [];
  const [tabValue, setTabValue] = useState(0);
  const taskPeriodicities = memo?.storage.taskPeriodicities || [];
  const defaultPeriodicity = taskPeriodicities?.filter(
    (p: any) => p.tag === ETaskPeriodicity.UNIQUE
  );
  const defaultStatus = taskStatuses?.filter(
    (t: any) => t.tag === ETaskStatus.NOT_STARTED
  );
  const defaultValues = {
    periodicity: defaultPeriodicity[0] || undefined,
    status: defaultStatus[0] || undefined,
    priority: ETaskPriority.MEDIUM,
    start: format(new Date(), Config.rangeDatePickerFormat),
    end: format(new Date(), Config.rangeDatePickerFormat),
  };
  const {
    register,
    formState,
    watch,
    control,
    getValues,
    setValue,
    handleSubmit,
    reset,
  } = useForm<any>({
    resolver: yupResolver(TaskValidationSchema),
    defaultValues: defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });
  const userId = localStorage.getItem("userId");
  const priorityColors: any = {
    [ETaskPriority.HIGH]: EPriorityColors.HIGH,
    [ETaskPriority.MEDIUM]: EPriorityColors.MEDIUM,
    [ETaskPriority.LOW]: EPriorityColors.LOW,
  };
  /**
   *
   */
  useEffect(() => {
    if (taskContext.taskId === "" && users && users?.length > 0) {
      reset(defaultValues);
    }
  }, [drawerStateContext.open]);

  /**
   * set disabled fields is user did not create this task and it's not admin
   */
  useEffect(() => {
    if (
      getValues("createdBy")?._id !== undefined &&
      getValues("createdBy")?._id !== userId &&
      getValues("assignedUsers")?.length > 0
    ) {
      setShouldFieldsBeDisabled(true);
    } else {
      setShouldFieldsBeDisabled(false);
    }
  }, [watch("_id")]);

  /**
   * Set connected user as assigned user if task type is specific
   */
  useEffect(() => {
    let connectedUser;
    const currentTaskType = getValues();
    switch (currentTaskType.type) {
      case ETaskType.SPECIFIC:
        if (!currentTaskType._id) {
          connectedUser = users?.filter((u: any) => u._id === userId);
          setValue("assignedUsers", [connectedUser[0]]);
        }
        break;
      default:
        break;
    }
  }, [watch("type")]);

  /**
   *  Toggle drawer
   */
  const toggleDrawer = () => {
    if (drawerStateContext.open) {
      taskContext.setTaskId("");
    }
    drawerStateContext.setOpen(false);
  };

  /**
   * Set task
   */
  useEffect(() => {
    if (taskContext.taskId !== "") {
      getTaskById(taskContext.taskId);
    }
  }, [taskContext.taskId]);

  /**
   *
   * @param id
   */
  const getTaskById = (id: string) => {
    getData(`${endpoints.task.main}/${id}`).then((res) => {
      if (!res || res?.data?.errors) return;
      drawerStateContext.setOpen(true);
      reset(res.data);
    });
  };

  /**
   *
   */
  useEffect(() => {
    getUsers();
  }, []);

  /**
   *
   */
  const getUsers = () => {
    getData(`${endpoints.user.autocomplete}`).then((res: AxiosResponse) => {
      if (!res || !res?.data) {
        return;
      }
      setUsers(res.data);
    });
  };

  /**
   *
   * @returns
   */
  const shouldRenderCreatedBy = () => {
    const type = getValues("type") || {};
    return (
      type === ETaskType.ASSIGNED && getValues("createdBy")?._id !== userId
    );
  };

  /**
   *
   */
  const handleChangeModalState = () => {
    setOpenModal(!openModal);
  };

  /**
   *
   * @param model
   */
  const updateTask = (model: any) => {
    updateData(`${endpoints.task.main}/${model._id}`, model).then(
      (res: AxiosResponse) => {
        if (!res || res?.data.errors) {
          return;
        }
        refreshContext.setRefresh(true);
        toggleDrawer();
      }
    );
  };

  /**
   *
   * @param model
   */
  const createTask = (model: any) => {
    const url = `${endpoints.task.main}`;
    postData(url, model).then((res: AxiosResponse) => {
      if (!res || res?.data.errors) {
        return;
      }
      refreshContext.setRefresh(true);
      toggleDrawer();
    });
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    model._id ? updateTask(model) : createTask(model);
  };

  /**
   *
   * @param event
   * @param newValue
   */
  const handleChangeTabValue = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTabValue(newValue);
  };

  /**
   *
   * @returns
   */
  const renderContentBasedOnTabs = () => {
    switch (tabValue) {
      case 0:
        return renderTask();
      case 1:
        return (
          <Alerts
            id={getValues("_id")}
            model={new TaskModel()}
            modelType={EModelTypes.TASKS}
            excludedFields={[
              "_id",
              "text",
              "description",
              "remarks",
              "status",
              "periodicity",
              "type",
              "start",
              "end",
              "skipWeekend",
              "finishDate",
              "priority",
              "createdAt",
              "updatedAt",
              "deletedAt",
              "isFlexible",
              "assignedUsers",
              "createdBy",
              "parent",
            ]}
          />
        );
      default:
        break;
    }
  };

  const renderTask = () => {
    return (
      <Grid container spacing={1} className={styles.drawerContent}>
        <PropertyContainer
          property={Vocabulary.fancyName}
          propertyInput={
            <TextField
              {...register("text")}
              id="text"
              name="text"
              fullWidth
              multiline
              rows={getNumberOfLinesFromText(getValues("text"))}
              error={typeof formState.errors.text === "object"}
              helperText={formState.errors.text?.message?.toString()}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          }
        />
        {getValues("parent") && (
          <PropertyContainer
            property={Vocabulary.primaryTask}
            propertyInput={
              <Button variant="text" color="dashboard">
                {getValues("parent")?.text}
              </Button>
            }
          />
        )}
        {shouldRenderCreatedBy() ? (
          <PropertyContainer
            property={Vocabulary.assignedBy}
            propertyInput={
              <Typography>{`${getValues("createdBy")?.lastName || ""} ${getValues("createdBy")?.firstName || ""
                }`}</Typography>
            }
          />
        ) : null}
        <PropertyContainer
          property={Vocabulary.type}
          propertyInput={
            <Controller
              name="type"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  className={styles.taskType}
                  fullWidth
                  onChange={(event, value) => field.onChange(value)}
                  value={getValues("type")}
                  options={Object.values(ETaskType)}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(option: any) => option}
                  id="type"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="type"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      inputRef={ref}
                      label={
                        !getValues("type") || getValues("type") === ""
                          ? Vocabulary.chooseType
                          : null
                      }
                    />
                  )}
                />
              )}
            />
          }
        />
        <PropertyContainer
          property={Vocabulary.assignedUser}
          propertyInput={
            <Controller
              name="assignedUsers"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  sx={{
                    maxHeight: 150,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  multiple
                  fullWidth
                  onChange={(event, value) => field.onChange(value)}
                  value={getValues("assignedUsers") || []}
                  options={users || []}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?.id === value?.id
                  }
                  getOptionLabel={(option) =>
                    `${option?.lastName ? option?.lastName : ""} ${option?.firstName ? option?.firstName : ""
                    }`
                  }
                  id="type"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="type"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      inputRef={ref}
                      label={
                        getValues("assignedUsers")?.length > 0
                          ? ""
                          : Vocabulary.noUserAssigned
                      }
                    />
                  )}
                  renderTags={(value: any, getTagProps: any) =>
                    value
                      ? value?.map((option: any, index: number) => (
                        <Chip
                          avatar={
                            <Avatar className={styles.userAvatar}>
                              {`${option?.lastName?.charAt(
                                0
                              )}${option?.firstName?.charAt(0)}`}
                            </Avatar>
                          }
                          variant="outlined"
                          label={`${option?.lastName ? option?.lastName : ""
                            } ${option?.firstName ? option.firstName : ""}`}
                          {...getTagProps({ index })}
                        />
                      ))
                      : null
                  }
                />
              )}
            />
          }
        />

        <PropertyContainer
          property={Vocabulary.priority}
          propertyInput={
            <Controller
              name="priority"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  fullWidth
                  onChange={(event, value) => field.onChange(value)}
                  value={getValues("priority")}
                  options={Object.values(ETaskPriority)}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(option: any) => option}
                  id="priority"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="priority"
                      variant="standard"
                      inputRef={ref}
                      label={
                        !getValues("priority") || getValues("priority") === ""
                          ? Vocabulary.choosePriority
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        startAdornment: !getValues("priority") ? null : (
                          <div
                            className={genericStyles.colorCircle}
                            style={{
                              backgroundColor:
                                priorityColors[getValues("priority")],
                            }}
                          />
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          }
        />

        <PropertyContainer
          property={Vocabulary.status}
          propertyInput={
            <Controller
              name="status"
              control={control}
              defaultValue={null}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  onChange={(event, value) => field.onChange(value)}
                  value={getValues("status")}
                  options={taskStatuses}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  fullWidth
                  getOptionLabel={(option: any) => {
                    return option?.name;
                  }}
                  id="status"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      name="status"
                      inputRef={ref}
                      variant="standard"
                      label={Vocabulary.chooseStatus}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        startAdornment: !getValues("status") ? null : (
                          <div
                            className={genericStyles.colorCircle}
                            style={{
                              backgroundColor: getValues("status")?.color,
                            }}
                          />
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          }
        />
        <PropertyContainer
          property={Vocabulary.observations}
          propertyInput={
            <TextField
              {...register("remarks")}
              id="remarks"
              name="remarks"
              fullWidth
              error={typeof formState.errors.remarks === "object"}
              helperText={formState.errors.remarks?.message?.toString()}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          }
        />
        <PropertyContainer
          property={Vocabulary.periodicity}
          propertyInput={
            <Chip
              disabled={
                getValues("isFlexible") === true
                  ? false
                  : shouldFieldsBeDisabled ||
                  (getValues("parent") !== null &&
                    getValues("parent") !== undefined)
              }
              label={
                !getValues("periodicity")
                  ? Vocabulary.choosePeriodicity
                  : getValues("periodicity")?.name
              }
              color={"secondary"}
              onClick={(event: any) => {
                setAnchorEl(event.currentTarget);
                setOpenPeriodicity(!openPeriodicity);
              }}
            />
          }
        />
        <Popover
          open={openPeriodicity}
          sx={{ zIndex: 2600 }}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={() => {
            setAnchorEl(null);
            setOpenPeriodicity(!openPeriodicity);
          }}
        >
          <TaskPeriodicity
            control={control}
            setOpenPeriodicity={setOpenPeriodicity}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            periodicities={taskPeriodicities}
          />
        </Popover>
        <PropertyContainer
          property={Vocabulary.flexibleTask}
          propertyInput={
            <Controller
              name="isFlexible"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  label={`(${Vocabulary.flexibleTaskObservations})`}
                  labelPlacement="end"
                  control={
                    <GreenSwitch
                      id="isFlexible"
                      name="isFlexible"
                      disabled={
                        shouldFieldsBeDisabled ||
                        (getValues("parent") !== null &&
                          getValues("parent") !== undefined)
                      }
                      onChange={(event) => {
                        onChange(event.target.checked);
                      }}
                      checked={value}
                      sx={{
                        color: themeContext.theme?.palette.primary.light,
                      }}
                    />
                  }
                />
              )}
            />
          }
        />
        <PropertyContainer
          property={Vocabulary.progress}
          propertyInput={
            <Controller
              name="progress"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Slider
                  max={100}
                  min={0}
                  defaultValue={0}
                  onChange={(event: any, value: any) => {
                    const newValue = value ? value / 100 : 0;
                    field.onChange(newValue);
                  }}
                  value={
                    getValues("progress")
                      ? parseFloat(getValues("progress")) * 100
                      : 0
                  }
                />
              )}
            />
          }
        />
        <Grid item xs={12} className={genericStyles.gridContainer}>
          <TextField
            {...register("description")}
            id="description"
            name="description"
            fullWidth
            multiline
            rows={4}
            label={Vocabulary.description}
            error={typeof formState.errors.description === "object"}
            helperText={formState.errors.description?.message?.toString()}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {getValues("_id") && (
            <FileExplorerMin
              shouldCRUDFile={true}
              name={ComponentsNames.FileExplorerMin}
              id={getValues("_id")}
              baseFolderPath={
                getValues("_id") ? `tasks/${getValues("_id")}` : undefined
              }
            />
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Drawer
      sx={{
        "& .MuiPaper-root": styles.drawerContainer,
      }}
      className={styles.drawerContainer}
      anchor={"right"}
      open={drawerStateContext.open}
      onClose={(event: object, reason: string) => {
        if (reason !== "backdropClick") {
          toggleDrawer();
        }
      }}
    >
      <form
        id={FormsIds.taskForm}
        className={styles.taskForm}
        onSubmit={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, onFormError)(e);
        }}
      >
        <Grid container spacing={1} className={styles.drawerHeader}>
          <Grid item xs={4}>
            {watch("_id") && (
              <Fragment>
                <Button
                  onClick={handleChangeModalState}
                  variant="contained"
                  color="secondary"
                  sx={{
                    backgroundColor: themeContext.theme?.palette.secondary.dark,
                  }}
                  className={styles.subtaskButton}
                >
                  {Vocabulary.subtasks}
                </Button>
              </Fragment>
            )}
          </Grid>

          <Grid item xs={1} container alignItems={"center"}>
            <CircularProgressWithLabel value={watch("progress")} />
          </Grid>
          <Grid item xs={6}>
            <Tabs
              value={tabValue}
              onChange={handleChangeTabValue}
              textColor="inherit"
              sx={{ width: "100%" }}
            >
              <Tab icon={<Home />} />
              {watch("_id") ? <Tab icon={<AddAlert />} /> : null}
            </Tabs>
          </Grid>
          <Grid
            item
            xs={1}
            container
            justifyContent={"flex-end"}
            alignItems={"center"}
          >
            <Tooltip title={Vocabulary.close}>
              <KeyboardTab
                color="secondary"
                className={styles.closeIcon}
                fontSize={"medium"}
                onClick={toggleDrawer}
              />
            </Tooltip>
          </Grid>
        </Grid>
        {renderContentBasedOnTabs()}
        <Grid container className={styles.drawerFooter}>
          <Grid item xs={12} className={styles.saveButtonContainer}>
            <Button type="submit" variant="contained">
              {Vocabulary.save}
            </Button>
          </Grid>
        </Grid>
      </form>
      <SubtasksModal
        statuses={taskStatuses}
        open={openModal}
        onClose={handleChangeModalState}
        parentTaskId={getValues("_id")}
        isParentFinished={getValues("status")?.name === Vocabulary.finished}
      />
    </Drawer>
  );
}
